import type { BlogFrontmatter } from "./types";

/**
 * Bild-Assets (Übergabe-Briefing Lauf `webinar-funnel`, final):
 * - Motiv 1 (OG/Thumbnail, 1200×630): Anmeldung ist noch keine Pipeline —
 *   Showrate und Termin als die beiden Zahlen, die zählen. Nur OG/Schema und
 *   Index-Karte, nicht im Fließtext.
 * - Motiv 2 (Inline, 1200×630): Die Pipeline entsteht nach dem Webinar —
 *   Nacharbeit für Teilnehmer und No-Shows.
 * - Bild 2a / 2b (1920×1080) gehören NICHT in den Artikel (Newsletter zu T).
 * Dateien unverändert lassen, nichts neu rendern.
 */
const MOTIV_1_OG = "/assets/blog/webinar-leadgenerierung-b2b/motiv-1-og.png";
const MOTIV_2_NACHARBEIT =
  "/assets/blog/webinar-leadgenerierung-b2b/motiv-2-nacharbeit.png";

export const frontmatter: BlogFrontmatter = {
  title: "Webinar-Leadgenerierung im B2B: Anmeldungen sind noch keine Pipeline",
  metaTitle: "Webinar-Leadgenerierung B2B: von Anmeldung zum Termin",
  slug: "webinar-leadgenerierung-b2b",
  description:
    "Webinar-Leadgenerierung im B2B: Warum 40 % Showrate und 10 % Termine aus Live-Teilnehmern die einzigen Zahlen sind, die zählen. Plus die richtige Nacharbeit.",
  primaryKeyword: "webinar leadgenerierung b2b",
  secondaryKeywords: [
    "webinar funnel b2b",
    "showrate webinar",
    "webinar nacharbeit",
  ],
  author: "tim",
  datePublished: "2026-09-22",
  dateModified: "2026-09-22",
  image: MOTIV_1_OG,
  imageAlt:
    "Webinar-Funnel im B2B: 40 Prozent Showrate und 10 Prozent Termine aus Live-Teilnehmern",
  internalLinks: [
    "/webinare",
    "/blog/linkedin-termine-generieren-telemarketing",
    "/blog/b2b-leadgenerierung-linkedin",
    "/blog/was-ist-revops-revenue-operations",
    "/booking",
  ],
  draft: false,
  readingMinutes: 7,
  kurzGesagt:
    "Webinar-Leadgenerierung im B2B wirkt, weil im Webinar Demand Generation und Lead Generation zusammenfallen: Sie setzen ein Thema in den Kopf Ihrer Zielgruppe und bekommen im selben Schritt die E-Mail-Adresse. Die Pipeline entsteht trotzdem nicht im Webinar, sondern in der Nacharbeit danach. Zwei Zahlen entscheiden darüber: 40 Prozent der Angemeldeten sollten live dabei sein, und 10 Prozent der Live-Teilnehmer sollten in einem Termin landen. Alles davor ist Reichweite.",
  faq: [
    {
      q: "Wie viele Anmeldungen braucht ein B2B-Webinar, damit es sich lohnt?",
      a: "Rechnen Sie rückwärts: Bei 40 Prozent Showrate und 10 Prozent Terminquote ergeben 100 Anmeldungen vier Termine. Wie viele Anmeldungen Sie brauchen, hängt also daran, wie viele Termine Sie aus dem Kanal erwarten, nicht an einer absoluten Mindestzahl.",
    },
    {
      q: "Was ist eine gute Showrate für ein B2B-Webinar?",
      a: "Wir steuern auf 40 Prozent. Darunter lohnt sich ein Blick auf die Strecke zwischen Anmeldung und Termin: Erinnerungen, Kalendereintrag und ein Grund, warum der Termin verteidigt wird.",
    },
    {
      q: "Sollte man die Aufzeichnung automatisch an alle Angemeldeten schicken?",
      a: "Nein. Bieten Sie sie an und warten Sie auf die Antwort. Die Antwort ist der Gesprächseinstieg, die automatische Zusendung ist das Ende des Kontakts.",
    },
    {
      q: "Wie schnell nach dem Webinar sollte die Nacharbeit starten?",
      a: "Innerhalb von 24 bis 48 Stunden, solange das Thema präsent ist. Teilnehmer telefonisch, No-Shows per E-Mail.",
    },
    {
      q: "Ist ein Webinar auch für kleine Zielgruppen sinnvoll?",
      a: "Gerade dann. Bei einer eng definierten Zielgruppe ist nicht die Reichweite entscheidend, sondern wer im Raum sitzt. Zwanzig Live-Teilnehmer aus Ihren Zielaccounts sind mehr wert als dreihundert aus der Gießkanne.",
    },
  ],
};

export default function Article() {
  return (
    <>
      <p>
        Die meisten Webinar-Auswertungen im B2B zeigen eine einzige Zahl:
        Anmeldungen. 180 Registrierungen, ein zufriedenes Marketing-Team, ein
        Screenshot im Reporting. Vier Wochen später fragt der Vertrieb, was daraus
        geworden ist, und niemand kann die Frage beantworten.
      </p>
      <p>
        Das liegt nicht am Format. Das Webinar ist der stärkste Kanal, den
        B2B-Marketing zur Verfügung hat. Es liegt daran, dass die meisten
        Unternehmen das Webinar als Event behandeln und nicht als Funnel. Ein Event
        ist am Abend vorbei. Ein Funnel fängt am Abend erst an.
      </p>

      <h2>Warum fallen im Webinar Demand Gen und Lead Gen zusammen?</h2>
      <p>
        In fast jedem anderen Kanal müssen Sie sich entscheiden. Demand Generation
        setzt Themen: Sie bringen jemanden dazu, sich mit einem Problem zu
        beschäftigen, von dem er vorher nicht wusste, dass er es hat. Lead
        Generation greift ab: Sie holen eine Kontaktadresse und übergeben sie an den
        Vertrieb. Das eine erzeugt Nachfrage, das andere erntet sie.
      </p>
      <p>
        Das Webinar macht beides in einem Termin. Sie zeigen 45 Minuten lang, dass
        ein Thema relevant ist und dass Sie es beherrschen. Genau dafür muss sich
        jemand vorher registrieren. Der Gedanke landet im Kopf, die Adresse landet
        im CRM, und beides passiert im selben Vorgang. Diese Kombination gibt es
        sonst nirgends.
      </p>

      <h2>Was misst ein Webinar-Funnel im B2B wirklich?</h2>
      <p>
        Ein Webinar-Funnel hat genau vier Stufen, und drei davon werden meistens
        nicht gemessen:
      </p>
      <ol>
        <li>
          <strong>Anmeldungen.</strong> Wie viele Menschen haben ihre Adresse
          gegeben.
        </li>
        <li>
          <strong>Live-Teilnehmer.</strong> Wie viele davon waren tatsächlich im
          Raum.
        </li>
        <li>
          <strong>Bearbeitete Kontakte.</strong> Wie viele wurden nach dem Webinar
          aktiv angegangen.
        </li>
        <li>
          <strong>Termine.</strong> Wie viele haben einem Gespräch zugestimmt.
        </li>
      </ol>
      <p>
        Wer nur Stufe 1 berichtet, berichtet eine Vanity-Metrik. Die Anmeldung
        kostet den Interessenten dreißig Sekunden und null Verbindlichkeit. Erst ab
        Stufe 2 reden Sie über Menschen, die Zeit investiert haben. Wie Sie diese
        Stufen sauber in Ihrem CRM abbilden, hängt an Ihrer
        Reporting-Architektur: dazu haben wir{" "}
        <a href="/de/blog/was-ist-revops-revenue-operations/">
          RevOps im Mittelstand
        </a>{" "}
        getrennt aufgeschrieben.
      </p>

      <h2>Welche Showrate ist im B2B realistisch?</h2>
      <p>
        Wir arbeiten mit einem Zielwert von <strong>40 Prozent</strong>. Von 100
        Anmeldungen sind 40 live dabei. Das ist kein Branchendurchschnitt, sondern
        der Wert, auf den wir unsere eigenen Webinare und die unserer Kunden
        steuern.
      </p>
      <p>
        40 Prozent klingt niedrig, wenn man Anmeldungen für bare Münze nimmt. Es ist
        aber die ehrlichere Zahl. Und sie ist der eigentliche Hebel: Jeder
        Prozentpunkt mehr Showrate ist ein Prozentpunkt mehr qualifizierte Kontakte,
        ohne dass Sie einen einzigen zusätzlichen Euro in die Bewerbung stecken.
      </p>
      <p>
        Fällt Ihre Showrate deutlich unter diesen Wert, liegt das fast nie am Thema.
        Es liegt an der Strecke zwischen Anmeldung und Termin: keine Erinnerung,
        kein Kalendereintrag, kein Grund, den Termin gegen ein spontanes Meeting zu
        verteidigen.
      </p>

      <h2>Wie viele Live-Teilnehmer werden zu Terminen?</h2>
      <p>
        Der zweite Zielwert:{" "}
        <strong>10 Prozent der Live-Teilnehmer landen in einem Termin.</strong>{" "}
        Nicht 10 Prozent der Anmeldungen. Bei 100 Anmeldungen, 40 Live-Teilnehmern
        und einer sauberen Nacharbeit sind das vier Termine pro Webinar.
      </p>
      <p>
        Vier Termine klingen wenig, bis Sie die Gegenrechnung aufmachen: Diese vier
        Menschen haben Ihnen 45 Minuten Aufmerksamkeit geschenkt, kennen Ihre
        Denkweise und haben ein konkretes Thema im Kopf. Das ist ein anderer
        Gesprächseinstieg als jeder Kaltkontakt. Und bei 14-tägiger Kadenz sind es
        acht Termine im Monat, aus einem einzigen Kanal.
      </p>

      <h2>
        Warum funktioniert das Webinar besonders bei erklärungsbedürftigen
        Produkten?
      </h2>
      <p>
        Je erklärungsbedürftiger Ihr Produkt, desto teurer ist der erste Termin.
        Niemand bucht ein Verkaufsgespräch für etwas, das er noch nicht einordnen
        kann. Das Webinar senkt genau diese Schwelle: Es kostet keine Verpflichtung,
        man kann die Kamera auslassen, und man lernt etwas, auch wenn man nie kauft.
      </p>
      <p>
        Deshalb registrieren sich für ein gutes Webinar deutlich mehr Menschen, als
        jemals ein Erstgespräch buchen würden. Sie kaufen sich mit dem Format Zugang
        zu einer Gruppe, die für einen direkten Vertriebskontakt noch nicht bereit
        ist, für ein Thema aber sehr wohl.
      </p>

      <figure className="not-prose my-10">
        <img
          src={MOTIV_2_NACHARBEIT}
          alt="Die Pipeline entsteht nach dem Webinar: Nacharbeit für Teilnehmer und No-Shows"
          width={1200}
          height={630}
          loading="lazy"
          className="w-full rounded-2xl"
        />
      </figure>

      <h2>Wie sieht die Nacharbeit bei Teilnehmern aus?</h2>
      <p>
        Hier entscheidet sich alles. Jeder Live-Teilnehmer wird nach dem Webinar{" "}
        <strong>telefonisch</strong> angegangen, ohne Ausnahme. Der Anruf hat eine
        feste Reihenfolge:
      </p>
      <ol>
        <li>
          <strong>Zuerst der Nutzen.</strong> Hat das Thema geholfen? Was davon war
          für die konkrete Situation relevant?
        </li>
        <li>
          <strong>Dann die Lage.</strong> Inwiefern beschäftigt Sie das Thema gerade
          im Unternehmen?
        </li>
        <li>
          <strong>Erst dann der Vorschlag.</strong> Wenn es passt: das Ganze noch
          einmal tiefer und auf Ihre Situation bezogen besprechen.
        </li>
      </ol>
      <p>
        Die Reihenfolge ist nicht Höflichkeit, sie ist der Grund, warum das Gespräch
        funktioniert. Wer mit dem Terminvorschlag einsteigt, bestätigt den Verdacht,
        dass das Webinar nur die Verpackung für einen Verkaufsanruf war. Wer mit dem
        Nutzen einsteigt, führt das Gespräch fort, das im Webinar begonnen hat. Wie
        diese Telefonstrecke im Detail aufgebaut ist, steht in unserem Beitrag zu{" "}
        <a href="/de/blog/linkedin-termine-generieren-telemarketing/">
          Terminen aus LinkedIn und Telemarketing
        </a>
        .
      </p>

      <h2>Wie gehen Sie mit No-Shows um?</h2>
      <p>
        60 Prozent der Angemeldeten waren nicht da. Diese Gruppe ist nicht verloren,
        sie hat nur einen anderen Einstieg verdient:{" "}
        <strong>erst E-Mail, nicht Telefon.</strong>
      </p>
      <p>
        Und eine Regel, die den Unterschied macht:{" "}
        <strong>
          Bieten Sie die Slides an, schicken Sie sie nicht ungefragt.
        </strong>{" "}
        Eine Mail mit angehängter Aufzeichnung ist erledigt, sobald sie gelesen ist.
        Eine Mail, die fragt, ob Sie die Unterlagen schicken dürfen, braucht eine
        Antwort. Diese Antwort ist der eigentliche Zweck der Mail.
      </p>
      <p>
        Jede Antwort, die zurückkommt, geht zurück in die Strecke: in die
        Telefonliste oder in eine weitere E-Mail-Aktivität. Ein No-Show, der
        antwortet, ist ab diesem Moment kein No-Show mehr, sondern ein Kontakt mit
        Gesprächseinstieg.
      </p>

      <h2>Woher kommen die Anmeldungen?</h2>
      <p>
        Aus zwei Quellen, die Sie bereits besitzen. Erstens <strong>LinkedIn</strong>
        : das Event, die Posts der beteiligten Personen, die Unternehmensseite und
        die direkte Ansprache relevanter Kontakte. Zweitens Ihr{" "}
        <strong>eigener Verteiler</strong>: Kontakte, die einmal Interesse gezeigt
        haben und seitdem nichts mehr von Ihnen gehört haben.
      </p>
      <p>
        Der zweite Punkt wird unterschätzt. Ein Webinar ist der beste Anlass, eine
        kalt gewordene Liste wieder anzusprechen, weil Sie etwas anbieten statt
        etwas zu verlangen. Wie Sie die Zielgruppe dafür sauber definieren, steht in
        unserem Beitrag zur{" "}
        <a href="/de/blog/b2b-leadgenerierung-linkedin/">
          B2B-Leadgenerierung über LinkedIn
        </a>
        .
      </p>

      <h2>Was bringt ein Webinar-Funnel über die Zeit?</h2>
      <p>
        Einzelne Webinare bringen einzelne Termine. Interessant wird es, wenn das
        Format zur Kadenz wird.
      </p>
      <p>
        Für <strong>BS Group Data Analytics</strong>, einen IT-Dienstleister aus
        Zürich mit 11 bis 50 Mitarbeitenden, haben wir den Webinar-Funnel gemeinsam
        mit Corporate Influencer und Lead Nurturing aufgebaut. Ergebnis über 24
        Monate: <strong>2,1 Millionen CHF Pipeline.</strong> Nicht aus einem Event,
        sondern aus einem wiederholten Ablauf mit fester Nacharbeit.
      </p>
      <p>
        Bei einer B2B-CRM-Plattform im DACH-Raum mit 50 bis 500 Mitarbeitenden haben
        wir den Webinar-Funnel auf die Trial-Akquise gelegt. Ergebnis:{" "}
        <strong>35 Prozent geringere Kosten pro Lead</strong> im Vergleich zu den bis
        dahin genutzten Kanälen.
      </p>
      <p>
        Beides sind keine Ausreißer, sondern das Ergebnis derselben Mechanik:
        konstante Kadenz, definierte Showrate, verbindliche Nacharbeit.
      </p>

      <h2>Welche Fehler kosten die Pipeline?</h2>
      <p>
        Fünf Fehler sehen wir immer wieder, und jeder einzelne ist teuer:
      </p>
      <ul>
        <li>
          <strong>Das Webinar wird als Event geplant, nicht als Funnel.</strong> Die
          Nacharbeit ist nicht besetzt, bevor die Bewerbung startet.
        </li>
        <li>
          <strong>Nur Anmeldungen werden berichtet.</strong> Showrate und Termine
          tauchen im Reporting gar nicht auf.
        </li>
        <li>
          <strong>Die Slides gehen ungefragt raus.</strong> Damit verschenken Sie
          den einzigen Grund, aus dem ein No-Show antworten würde.
        </li>
        <li>
          <strong>Der Anruf beginnt mit dem Terminvorschlag.</strong> Das Gespräch
          ist beendet, bevor es begonnen hat.
        </li>
        <li>
          <strong>Das Webinar ist einmalig.</strong> Ein Format ohne Kadenz baut
          keinen Verteiler auf und kein Publikum.
        </li>
      </ul>

      <h2>Der nächste Schritt</h2>
      <p>
        Ein Webinar-Funnel ist kein Event mit Anmeldeformular, sondern ein
        wiederkehrender Ablauf mit zwei Zielwerten und einer verbindlichen
        Nacharbeit. Wenn Sie wissen wollen, wie dieser Ablauf für Ihr Thema und Ihre
        Zielaccounts aussieht: Unsere{" "}
        <a href="/de/webinare/">Webinar-Leadgenerierung</a> baut genau diese Strecke
        auf, von der Zielliste über die Bewerbung bis zur telefonischen Nacharbeit.
      </p>
    </>
  );
}
